import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { 
  Shield, Lock, CheckCircle2, ChevronRight, 
  BookOpen, Target, Calendar, Flame, Award,
  ClipboardList, Send, Loader2, X, AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Componente del bloque bloqueado en el dashboard
export const GuardianLockedBlock = ({ onRequestAccess }) => {
  const [showModal, setShowModal] = useState(false);

  return (
    <>
      <Card 
        className="bg-zinc-900/50 border-zinc-700/50 cursor-pointer hover:border-amber-500/30 transition-all"
        onClick={() => setShowModal(true)}
        data-testid="guardian-locked-block"
      >
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-amber-500/10">
                <Shield className="h-8 w-8 text-amber-500" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-xl font-bold text-foreground">Objetivo Guardián</h3>
                  <Lock className="h-4 w-4 text-zinc-500" />
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  La vía formativa del sistema
                </p>
              </div>
            </div>
            <ChevronRight className="h-6 w-6 text-zinc-600" />
          </div>
        </CardContent>
      </Card>

      <GuardianAccessModal 
        open={showModal} 
        onClose={() => setShowModal(false)}
        onRequestAccess={onRequestAccess}
      />
    </>
  );
};

// Modal de acceso restringido
export const GuardianAccessModal = ({ open, onClose, onRequestAccess }) => {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    nombre: '',
    motivo: '',
    experiencia_previa: '',
    disponibilidad: '',
    interes_formarse: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!formData.nombre || !formData.motivo || !formData.interes_formarse) {
      toast.error('Completa los campos obligatorios');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/me/guardian-request-access`, formData);
      toast.success('Solicitud enviada correctamente');
      setShowForm(false);
      onClose();
      if (onRequestAccess) onRequestAccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar solicitud');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-400">
            <Shield className="h-5 w-5" />
            Objetivo Guardián
          </DialogTitle>
        </DialogHeader>

        {!showForm ? (
          <div className="space-y-6 py-4">
            <div className="flex items-center gap-3 p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
              <Lock className="h-6 w-6 text-amber-500 flex-shrink-0" />
              <div>
                <p className="text-sm text-foreground font-medium">Acceso restringido</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Para desbloquear esta vía, debes completar primero un plan del sistema 
                  o solicitar validación directa a la Oficina del Guardián.
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <Button 
                variant="outline" 
                className="w-full justify-start border-zinc-700 hover:border-primary"
                onClick={() => { onClose(); navigate('/client/plans'); }}
              >
                <ClipboardList className="h-4 w-4 mr-3" />
                Ver planes disponibles
              </Button>
              
              <Button 
                className="w-full justify-start bg-amber-600 hover:bg-amber-700"
                onClick={() => setShowForm(true)}
              >
                <Send className="h-4 w-4 mr-3" />
                Solicitar validación
              </Button>
              
              <Button 
                variant="ghost" 
                className="w-full text-muted-foreground"
                onClick={onClose}
              >
                Cerrar
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4 py-4">
            <p className="text-sm text-muted-foreground">
              Completa este formulario para solicitar acceso directo al Objetivo Guardián.
            </p>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre completo *</Label>
                <Input
                  value={formData.nombre}
                  onChange={(e) => setFormData({...formData, nombre: e.target.value})}
                  placeholder="Tu nombre"
                  className="bg-zinc-800/50 border-zinc-700"
                />
              </div>

              <div className="space-y-2">
                <Label>¿Por qué quieres acceder? *</Label>
                <Textarea
                  value={formData.motivo}
                  onChange={(e) => setFormData({...formData, motivo: e.target.value})}
                  placeholder="Explica tu motivación..."
                  className="bg-zinc-800/50 border-zinc-700 min-h-[80px]"
                />
              </div>

              <div className="space-y-2">
                <Label>Experiencia previa en entrenamiento</Label>
                <Textarea
                  value={formData.experiencia_previa}
                  onChange={(e) => setFormData({...formData, experiencia_previa: e.target.value})}
                  placeholder="Años entrenando, certificaciones, etc."
                  className="bg-zinc-800/50 border-zinc-700 min-h-[60px]"
                />
              </div>

              <div className="space-y-2">
                <Label>Disponibilidad semanal</Label>
                <Input
                  value={formData.disponibilidad}
                  onChange={(e) => setFormData({...formData, disponibilidad: e.target.value})}
                  placeholder="Ej: 4-5 días por semana"
                  className="bg-zinc-800/50 border-zinc-700"
                />
              </div>

              <div className="space-y-2">
                <Label>¿Qué te interesa de formarte y ayudar dentro del sistema? *</Label>
                <Textarea
                  value={formData.interes_formarse}
                  onChange={(e) => setFormData({...formData, interes_formarse: e.target.value})}
                  placeholder="Tu interés en la formación y comunidad..."
                  className="bg-zinc-800/50 border-zinc-700 min-h-[80px]"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setShowForm(false)}
              >
                Volver
              </Button>
              <Button 
                className="flex-1 bg-amber-600 hover:bg-amber-700"
                onClick={handleSubmit}
                disabled={submitting}
              >
                {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                Enviar solicitud
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Bloque desbloqueado para usuarios con acceso
export const GuardianUnlockedBlock = ({ guardianData }) => {
  const navigate = useNavigate();
  const pilot = guardianData?.pilot_data || {};
  const progressPercent = pilot.logs_completed ? Math.round((pilot.logs_completed / 35) * 100) : 0;

  return (
    <Card 
      className="bg-gradient-to-br from-amber-500/10 via-orange-500/5 to-transparent border-amber-500/30 cursor-pointer hover:border-amber-500/50 transition-all"
      onClick={() => navigate('/client/guardian')}
      data-testid="guardian-unlocked-block"
    >
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-full bg-amber-500/20">
              <Shield className="h-8 w-8 text-amber-400" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-xl font-bold text-foreground">Objetivo Guardián</h3>
                <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/20 text-amber-400">
                  ASPIRANTE
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Semana {guardianData?.current_week || 1} · Día {guardianData?.current_day || 1}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-amber-400">{progressPercent}%</p>
            <p className="text-xs text-muted-foreground">completado</p>
          </div>
        </div>
        <Progress value={progressPercent} className="h-2 mt-4 bg-amber-950" />
      </CardContent>
    </Card>
  );
};

// Componente principal que decide qué mostrar
const GuardianBlock = () => {
  const [loading, setLoading] = useState(true);
  const [guardianStatus, setGuardianStatus] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/me/guardian-status`);
      setGuardianStatus(response.data);
      setError(false);
    } catch (error) {
      console.error('Error fetching guardian status:', error);
      setError(true);
      // No bloquear - mostrar como bloqueado por defecto
      setGuardianStatus({ has_access: false, status: 'locked' });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="p-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-amber-500" />
        </CardContent>
      </Card>
    );
  }

  // Si hay error, no mostrar el bloque para no romper la página
  if (error) {
    return null;
  }

  if (guardianStatus?.has_access) {
    return <GuardianUnlockedBlock guardianData={guardianStatus} />;
  }

  return <GuardianLockedBlock onRequestAccess={fetchStatus} />;
};

export default GuardianBlock;
